import { Link } from "@tanstack/react-router"
import { Menu } from "lucide-react"
import type { RefObject } from "react"
import { useState } from "react"
import { NavbarAuth } from "@/components/navbar-auth"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"

export function Brand() {
  return (
    <span className="brand">
      <span className="brand-mark" aria-hidden="true">
        <img src="/dragon.svg" width="34" height="34" alt="" />
      </span>
      into<span className="brand-word">short</span>
    </span>
  )
}

function PublicNavigation({
  mobile = false,
  onNavigate,
}: {
  mobile?: boolean
  onNavigate?: () => void
}) {
  return (
    <NavigationMenu
      className={mobile ? "w-full max-w-none justify-start" : undefined}
      orientation={mobile ? "vertical" : "horizontal"}
    >
      <NavigationMenuList
        className={mobile ? "w-full flex-col items-stretch" : undefined}
      >
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className={
              mobile
                ? "public-nav-label w-full justify-between"
                : "public-nav-label"
            }
          >
            Resources
          </NavigationMenuTrigger>
          <NavigationMenuContent className={mobile ? "w-full" : undefined}>
            <ul className="flex min-w-40 flex-col gap-1">
              <li>
                <NavigationMenuLink
                  render={<a href="/#questions" />}
                  onClick={onNavigate}
                >
                  FAQ
                </NavigationMenuLink>
              </li>
              <li>
                <NavigationMenuLink
                  render={<Link to="/blog" />}
                  onClick={onNavigate}
                >
                  Blog
                </NavigationMenuLink>
              </li>
              <li>
                <NavigationMenuLink
                  render={<Link to="/roadmap" />}
                  onClick={onNavigate}
                >
                  Roadmap
                </NavigationMenuLink>
              </li>
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuLink
            render={<Link to="/pricing" />}
            className={buttonVariants({
              variant: "ghost",
              className: "public-nav-label",
            })}
            onClick={onNavigate}
          >
            Pricing
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  )
}

type PublicNavbarProps = {
  headerRef?: RefObject<HTMLElement | null>
  editorTheme?: boolean
}

export function PublicNavbar({
  headerRef,
  editorTheme = false,
}: PublicNavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header
      ref={headerRef}
      className="landing-header page-width"
      data-editor-theme={editorTheme ? "true" : undefined}
    >
      <Link to="/" reloadDocument aria-label="intoshort, home">
        <Brand />
      </Link>
      <nav className="public-desktop-nav" aria-label="Main navigation">
        <PublicNavigation />
      </nav>
      <div className="public-navbar-actions">
        <NavbarAuth />
        <Drawer open={menuOpen} onOpenChange={setMenuOpen} showSwipeHandle>
          <DrawerTrigger
            className="public-mobile-menu"
            render={<Button variant="ghost" size="icon-lg" />}
            aria-label="Open navigation menu"
          >
            <Menu />
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Menu</DrawerTitle>
            </DrawerHeader>
            <nav aria-label="Mobile navigation" className="p-4">
              <PublicNavigation mobile onNavigate={() => setMenuOpen(false)} />
            </nav>
            <DrawerFooter>
              <DrawerClose render={<Button variant="outline" />}>
                Close
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </div>
    </header>
  )
}
